import { useState, useEffect } from 'react';

interface NavbarProps {
  activeSection: string;
  setActiveSection: (section: string) => void;
  favoritesCount: number;
  onOpenFavorites: () => void;
}

export default function Navbar({ activeSection, setActiveSection, favoritesCount, onOpenFavorites }: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [announcementDismissed, setAnnouncementDismissed] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Date actuelle le 3 août, l'annonce est encore pertinente
  const today = new Date();
  const vacationEnd = new Date('2026-09-07');
  const showAnnouncement = !announcementDismissed && today <= vacationEnd;

  return (
    <>
      {/* Vacances d'été announcement bar */}
      {showAnnouncement && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-chocolate text-cream">
          <div className="max-w-7xl mx-auto px-4 py-1.5 flex items-center justify-center gap-2.5">
            <div className="w-5 h-5 flex items-center justify-center shrink-0">
              <i className="ri-sun-fill text-sm text-cream"></i>
            </div>
            <p className="text-xs font-medium text-cream/90 text-center">
              <strong>Fermeture estivale</strong> — La pizzeria sera fermée du <strong>10 août au 7 septembre</strong> inclus. Réouverture le mardi 8 septembre !
            </p>
            <button
              onClick={() => setAnnouncementDismissed(true)}
              className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-cream/10 transition-colors cursor-pointer shrink-0 ml-1"
              aria-label="Fermer"
            >
              <i className="ri-close-line text-sm text-cream/70 hover:text-cream"></i>
            </button>
          </div>
        </div>
      )}

      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        showAnnouncement ? 'top-[32px]' : 'top-0'
      } ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}>
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a
            href="#menu"
            onClick={(e) => {
              e.preventDefault();
              const menuElement = document.getElementById('menu');
              if (menuElement) {
                menuElement.scrollIntoView({ behavior: 'smooth' });
              }
              setActiveSection('menu');
            }}
            className="flex items-center gap-2 cursor-pointer group"
          >
            <span className="font-display text-xl font-bold text-chocolate group-hover:text-terracotta transition-colors">
              Pizz&apos;Ajo
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <a
              href="#home"
              onClick={(e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
                setActiveSection('home');
              }}
              className={`text-sm font-medium transition-colors cursor-pointer ${
                activeSection === 'home' ? 'text-terracotta' : 'text-chocolate hover:text-terracotta'
              }`}
            >
              Accueil
            </a>
            <a
              href="#menu"
              onClick={(e) => {
                e.preventDefault();
                const menuElement = document.getElementById('menu');
                if (menuElement) {
                  menuElement.scrollIntoView({ behavior: 'smooth' });
                }
                setActiveSection('menu');
              }}
              className={`text-sm font-medium transition-colors cursor-pointer ${
                activeSection === 'menu' ? 'text-terracotta' : 'text-chocolate hover:text-terracotta'
              }`}
            >
              Notre Carte
            </a>
            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                const contactElement = document.getElementById('contact');
                if (contactElement) {
                  contactElement.scrollIntoView({ behavior: 'smooth' });
                }
                setActiveSection('contact');
              }}
              className={`text-sm font-medium transition-colors cursor-pointer ${
                activeSection === 'contact' ? 'text-terracotta' : 'text-chocolate hover:text-terracotta'
              }`}
            >
              Contact
            </a>

            {/* Favorites Button */}
            <button
              onClick={onOpenFavorites}
              className="relative flex items-center gap-2 px-4 py-2 bg-terracotta hover:bg-chocolate text-white rounded-full transition-all shadow-md hover:shadow-lg cursor-pointer whitespace-nowrap"
            >
              <i className="ri-heart-3-fill text-base"></i>
              <span className="text-sm font-medium">Mes Préférences</span>
              {favoritesCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-cream text-terracotta w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center shadow-lg">
                  {favoritesCount}
                </span>
              )}
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center bg-terracotta/10 rounded-lg cursor-pointer"
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl text-terracotta`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 bg-dark/40 backdrop-blur-sm z-40 md:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <div className="md:hidden fixed top-[60px] left-0 right-0 bg-white shadow-lg z-50 border-t border-chocolate/10">
            <div className="px-4 py-4 space-y-2">
              <a
                href="#home"
                onClick={(e) => {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  setActiveSection('home');
                  setIsMobileMenuOpen(false);
                }}
                className={`block px-4 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                  activeSection === 'home' ? 'bg-terracotta/10 text-terracotta' : 'text-chocolate hover:bg-rose-soft'
                }`}
              >
                <i className="ri-home-line mr-2"></i>
                Accueil
              </a>
              <a
                href="#menu"
                onClick={(e) => {
                  e.preventDefault();
                  const menuElement = document.getElementById('menu');
                  if (menuElement) {
                    menuElement.scrollIntoView({ behavior: 'smooth' });
                  }
                  setActiveSection('menu');
                  setIsMobileMenuOpen(false);
                }}
                className={`block px-4 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                  activeSection === 'menu' ? 'bg-terracotta/10 text-terracotta' : 'text-chocolate hover:bg-rose-soft'
                }`}
              >
                <i className="ri-restaurant-line mr-2"></i>
                Notre Carte
              </a>
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  const contactElement = document.getElementById('contact');
                  if (contactElement) {
                    contactElement.scrollIntoView({ behavior: 'smooth' });
                  }
                  setActiveSection('contact');
                  setIsMobileMenuOpen(false);
                }}
                className={`block px-4 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                  activeSection === 'contact' ? 'bg-terracotta/10 text-terracotta' : 'text-chocolate hover:bg-rose-soft'
                }`}
              >
                <i className="ri-phone-line mr-2"></i>
                Contact
              </a>

              <div className="pt-2 border-t border-chocolate/10">
                <button
                  onClick={() => {
                    onOpenFavorites();
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full relative flex items-center gap-2 px-4 py-2.5 bg-terracotta hover:bg-chocolate text-white rounded-lg transition-all cursor-pointer"
                >
                  <i className="ri-heart-3-fill text-base"></i>
                  <span className="text-sm font-medium">Mes Préférences</span>
                  {favoritesCount > 0 && (
                    <span className="ml-auto bg-white text-terracotta px-2 py-0.5 rounded-full text-xs font-bold">
                      {favoritesCount}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </nav>
    </>
  );
}
